// src/services/userService.js
import apiClient from './api';

export const userService = {
  /**
   * Get all users
   * @returns {Promise<Object>} Result with success status and users data
   */
  getUsers: async (params = {}) => {
    try {
      const response = await apiClient.get('/users/list/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch users'
      };
    }
  },

  /**
   * Get all users (alias for getUsers for compatibility)
   * @returns {Promise<Object>} Result with success status and users data
   */
  getAllUsers: async () => {
    return userService.getUsers();
  },

  /**
   * Get a specific user by ID
   * @param {number} userId - ID of the user to fetch
   * @returns {Promise<Object>} Result with success status and user data
   */
  getUser: async (userId) => {
    try {
      const response = await apiClient.get(`/users/${userId}/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch user'
      };
    }
  },

  /**
   * Get payroll Employees not yet linked to a User (for the Employee dropdown).
   * Pass userId when editing so the currently-linked employee stays selectable.
   * @param {number} [userId]
   * @returns {Promise<Object>} Result with success status and employees data
   */
  getUnlinkedEmployees: async (userId = null) => {
    try {
      const params = userId ? { user_id: userId } : {};
      const response = await apiClient.get('/users/employees/unlinked/', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch employees'
      };
    }
  },

  /**
   * Create a new user
   * @param {Object} userData - The user data to create
   * @returns {Promise<Object>} Result with success status and created user data
   */
  createUser: async (userData) => {
    try {
      const response = await apiClient.post('/users/create/', userData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create user'
      };
    }
  },

  /**
   * Update an existing user
   * @param {number} userId - ID of the user to update
   * @param {Object} userData - The user data to update
   * @returns {Promise<Object>} Result with success status and updated user data
   */
  updateUser: async (userId, userData) => {
    try {
      const response = await apiClient.put(`/users/${userId}/update/`, userData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update user'
      };
    }
  },

  /**
   * Delete a user
   * @param {number} userId - ID of the user to delete
   * @returns {Promise<Object>} Result with success status
   */
  deleteUser: async (userId) => {
    try {
      await apiClient.delete(`/users/${userId}/delete/`);
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete user'
      };
    }
  },

  /**
   * Get available user groups/roles
   * @returns {Promise<Object>} Result with success status and groups data
   */
  getUserGroups: async () => {
    try {
      const response = await apiClient.get('/users/groups/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch user groups'
      };
    }
  },

  /**
   * Reset a user's password
   * @param {number} userId - ID of the user
   * @returns {Promise<Object>} Result with success status and temporary password
   */
  resetPassword: async (userId) => {
    try {
      const response = await apiClient.post(`/users/${userId}/reset-password/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password'
      };
    }
  },

  /**
   * Change user active status (enable/disable user)
   * @param {number} userId - ID of the user
   * @param {boolean} isActive - Whether to enable or disable the user
   * @returns {Promise<Object>} Result with success status
   */
  setUserActiveStatus: async (userId, isActive) => {
    try {
      const response = await apiClient.patch(`/users/${userId}/set-active/`, {
        is_active: isActive
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update user status'
      };
    }
  },

  /**
   * Apply a bulk action to multiple users
   * @param {number[]} userIds - IDs of the users to act on
   * @param {string} action - 'change_roles' | 'activate' | 'deactivate' | 'enable_qr' | 'disable_qr'
   * @param {Object} [params] - Action params (for change_roles: { mode: 'add'|'replace', group_ids: number[] })
   * @returns {Promise<Object>} Result with success status and summary
   */
  bulkAction: async (userIds, action, params = {}) => {
    try {
      const response = await apiClient.post('/users/bulk-action/', {
        user_ids: userIds,
        action,
        params
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Bulk action failed'
      };
    }
  },

  /**
   * Generate QR code for user login using codes API
   * @param {number} userId - ID of the user
   * @returns {Promise<Object>} Result with success status and QR code data
   */
  generateUserQR: async (userId) => {
    try {
      const response = await apiClient.post('/codes/generate/', {
        entity_type: 'user',
        entity_id: userId,
        format: 'QR',
        description: 'User login QR code'
      });
      
      if (response.data.success) {
        // Get the QR code image
        const codeId = response.data.code.id;
        const imageResponse = await apiClient.get(`/codes/${codeId}/image/`, {
          responseType: 'blob'
        });
        
        // Convert blob to base64
        const blob = new Blob([imageResponse.data]);
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        
        return {
          success: true,
          data: {
            qr_code_image: base64,
            code_id: codeId,
            code_value: response.data.code.value,
            user_id: userId
          }
        };
      } else {
        throw new Error(response.data.error || 'Failed to generate code');
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to generate QR code'
      };
    }
  },

  /**
   * Authenticate user using QR code via codes scan endpoint
   * @param {string} codeValue - QR code value (JSON string)
   * @returns {Promise<Object>} Result with success status and auth data
   */
  qrLogin: async (codeValue) => {
    try {
      const response = await apiClient.post('/codes/scan/', {
        code_value: codeValue,
        scan_app: 'user_login',
        scan_location: 'login_page'
      });

      if (response.data.success && response.data.auth_data) {
        return {
          success: true,
          data: response.data.auth_data
        };
      } else {
        throw new Error(response.data.error || 'Authentication failed');
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'QR login failed'
      };
    }
  },


  getEmailResolution: async () => {
    try {
      const response = await apiClient.get('/users/email-resolution/');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to get email resolution' };
    }
  }
};

export default userService;